import * as Sentry from '@sentry/react';
import { useLocalStorageState } from 'ahooks';
import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useHistory } from 'react-router-dom';

import paths from '../../paths.json';
import { useSearchQuery } from '../../utils/location';
import { useAuthentication } from '../AuthenticationProvider';
import Loading from '../Loading';

import { AdvertisersQuery, useAdvertisersQuery } from './Advertisers.generated';

type Advertiser = AdvertisersQuery['partnerAdvertisers'][number];

type AdvertiserTuple = [
  Advertiser,
  {
    advertisers: Advertiser[];
    setAdvertiserId: (id: string) => void;
  },
];

const AdvertiserContext = createContext<AdvertiserTuple | undefined>(undefined);

export const useAdvertiser = (): AdvertiserTuple => {
  const tuple = useContext(AdvertiserContext);

  if (!tuple) {
    throw new Error('No <PublisherProvider />');
  }

  return tuple;
};

export const useAdvertiserId = (): string => useAdvertiser()[0].id;

const key = 'partner-advertiser-id';

const AdvertiserProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const history = useHistory();
  const { searchQuery, updateSearchQuery } = useSearchQuery<{
    advertiserId?: string;
  }>();
  const { accessToken } = useAuthentication();

  const [advertiserId, setAdvertiserId] = useLocalStorageState(key, {
    defaultValue: searchQuery.advertiserId,
  });

  const { previousData, data = previousData, loading } = useAdvertisersQuery();

  const advertisers = useMemo(
    () => data?.partnerAdvertisers || [],
    [data?.partnerAdvertisers],
  );

  const selectedAdvertiser = useMemo(
    () => advertisers?.find(({ id }) => id === advertiserId) || advertisers[0],
    [advertiserId, advertisers],
  );

  useEffect(() => {
    if (selectedAdvertiser?.id && advertiserId !== selectedAdvertiser?.id) {
      setAdvertiserId(selectedAdvertiser.id);
    }
  }, [advertiserId, selectedAdvertiser?.id, setAdvertiserId]);

  useEffect(() => {
    if (searchQuery.advertiserId) {
      setAdvertiserId(searchQuery.advertiserId);
      updateSearchQuery({
        advertiserId: undefined,
      });
    }
  });

  const noOrganization = data?.partnerAdvertisers.length === 0;

  useEffect(() => {
    if (noOrganization) {
      Sentry.withScope((scope) => {
        scope.setExtras({
          accessToken,
          advertiserId,
          data,
          searchQuery,
        });
        Sentry.captureException(new Error('Access denied'));
      });
      history.replace(paths.login, {
        error: 'Access denied.',
      });
    }
  }, [accessToken, data, history, noOrganization, searchQuery, advertiserId]);

  if (loading) {
    return <Loading />;
  }

  if (!data || noOrganization) {
    return null;
  }

  return (
    <AdvertiserContext.Provider
      value={[
        selectedAdvertiser,
        {
          advertisers,
          setAdvertiserId,
        },
      ]}
    >
      {children}
    </AdvertiserContext.Provider>
  );
};

AdvertiserProvider.displayName = 'AdvertiserProvider';

export default AdvertiserProvider;
